/**
 * <storefinder-app> — Händlersuche (§11).
 *
 * Lädt Händler aus der eigenen PHP-API, zeigt sie auf einer Google-Karte und in
 * einer nach Entfernung sortierten Liste. PLZ-Suche läuft über den serverseitigen
 * Geocode-Proxy (Key bleibt verborgen); dessen Land triggert zugleich die
 * Verfügbarkeits-Engine (delivery-core) → Online-Lieferdatum als Synergie/Fallback.
 */

import { calculateDelivery, regionForCountry } from '@theme/delivery-core';

class StorefinderApp extends HTMLElement {
  connectedCallback() {
    const d = this.dataset;
    this.apiBase = (d.apiBase || '').replace(/\/$/, '');
    this.mapsEnabled = d.maps === '1';
    this.center = { lat: parseFloat(d.centerLat) || 47.8, lng: parseFloat(d.centerLng) || 11.4 };
    this.zoom = parseInt(d.zoom, 10) || 6;
    this.nearKm = parseFloat(d.nearKm) || 50;
    this.daysByRegion = {
      DE: parseInt(d.daysDe, 10) || 2,
      AT: parseInt(d.daysAt, 10) || 3,
      EU: parseInt(d.daysEu, 10) || 4,
    };
    this.cutoffHour = parseInt(d.cutoff, 10) || 13;
    this.hideOverHours = parseFloat(d.hideOver) || 20;
    this.holidays = (d.holidays || '').split(/[\s,;]+/).filter(Boolean);
    this.tz = d.tz || 'Europe/Berlin';
    this.visitorCountry = (d.country || 'DE').toUpperCase();
    this.autoLocate = d.autoLocate === '1';

    this.refs = {};
    this.querySelectorAll('[ref]').forEach((el) => { this.refs[el.getAttribute('ref')] = el; });

    this.dealers = [];
    this.filtered = [];
    this.origin = null;     // {lat,lng} der Nutzersuche
    this.markers = [];
    this.map = null;

    this.bind();
    this.loadDealers();
    if (this.mapsEnabled) this.initMap();
  }

  bind() {
    const { searchBtn, geoBtn, query, product, type, season, channel } = this.refs;
    searchBtn?.addEventListener('click', () => this.search());
    query?.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.search(); } });
    geoBtn?.addEventListener('click', () => this.useGeolocation());
    this.refs.filterBtn?.addEventListener('click', () => {
      const panel = this.refs.filterPanel; if (!panel) return;
      panel.hidden = !panel.hidden;
      this.refs.filterBtn.setAttribute('aria-expanded', String(!panel.hidden));
    });
    [product, type, season, channel].forEach((sel) => sel?.addEventListener('change', () => this.applyFilters()));
  }

  /** Anzahl aktiver Filter (abweichend vom Standard) neben „Filter" anzeigen. */
  updateFilterCount() {
    const el = this.refs.filterCount;
    if (!el) return;
    const active = [
      this.refs.product?.value,
      this.refs.type?.value,
      this.refs.channel?.value,
      (this.refs.season?.value || 'now') !== 'now' ? 'season' : '',
    ].filter(Boolean).length;
    el.textContent = String(active);
    el.hidden = active === 0;
  }

  /**
   * Beim Laden: Standort erfragen (S3). Ist die Freigabe schon erteilt, läuft es still;
   * bei „prompt" fragt der Browser einmal; bei Ablehnung bleibt die Länder-Gruppierung.
   */
  async autoLocateOnce() {
    if (!this.autoLocate || this.origin || !navigator.geolocation) return;
    try {
      if (navigator.permissions?.query) {
        const st = await navigator.permissions.query({ name: 'geolocation' });
        if (st.state === 'denied') return;
      }
    } catch (e) { /* Permissions-API nicht verfügbar → trotzdem fragen */ }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.quietFallback = true; // beim automatischen Start nicht zum Online-Kasten springen
        this.afterOrigin(null);
        this.quietFallback = false;
      },
      () => { /* abgelehnt oder fehlgeschlagen: stille Länder-Gruppierung bleibt */ },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 600000 }
    );
  }

  /** Ohne Standort: Händler im Land des Besuchers zuerst, dann übrige Länder, jeweils alphabetisch. */
  sortByCountry() {
    const home = this.visitorCountry;
    const rank = (x) => {
      const c = (x.country || 'DE').toUpperCase();
      if (c === home) return 0;
      if (c === 'DE' || c === 'AT' || c === 'CH') return 1;
      return 2;
    };
    this.filtered.sort((a, b) => rank(a) - rank(b) || (a.name || '').localeCompare(b.name || '', 'de'));
  }

  setStatus(msg) { if (this.refs.status) this.refs.status.textContent = msg || ''; }

  async loadDealers() {
    this.setStatus('Händler werden geladen …');
    try {
      const res = await fetch(`${this.apiBase}/dealers.php`, { headers: { Accept: 'application/json' } });
      const data = await res.json();
      if (!data || !data.ok) throw new Error('bad response');
      this.dealers = data.dealers || [];
      this.meta = data.meta || {};
      this.setStatus('');
      this.applyFilters();
      this.autoLocateOnce();
    } catch (err) {
      this.setStatus('Händlerliste konnte nicht geladen werden.');
    }
  }

  applyFilters() {
    const product = this.refs.product?.value || '';
    const type = this.refs.type?.value || '';
    const season = this.refs.season?.value || '';
    const channel = this.refs.channel?.value || '';

    this.filtered = this.dealers.filter((x) => {
      if (product && !(x.products || []).includes(product)) return false;
      if (type && x.type !== type) return false;
      if (season && !seasonMatches(season, x.seasonality)) return false;
      if (channel === 'online' && !x.online) return false;
      if (channel === 'local' && x.online) return false;
      return true;
    });

    if (this.origin) this.sortByDistance(); else this.sortByCountry();
    this.updateFilterCount();
    this.render();
  }

  sortByDistance() {
    if (!this.origin) return;
    this.filtered.forEach((x) => {
      x._dist = (x.lat != null && x.lng != null)
        ? haversineKm(this.origin.lat, this.origin.lng, x.lat, x.lng)
        : null;
    });
    // Online-Händler ohne Koordinaten ans Ende (sie liefern überallhin).
    this.filtered.sort((a, b) => (a._dist ?? Infinity) - (b._dist ?? Infinity));
  }

  render() {
    const list = this.refs.list;
    if (!list) return;
    list.innerHTML = '';

    if (!this.filtered.length) {
      list.innerHTML = '<li class="storefinder__item">Keine Händler für diese Auswahl. Bestell bequem online.</li>';
    }

    const typeLabels = (this.meta && this.meta.types) || {};
    const productLabels = (this.meta && this.meta.products) || {};

    this.filtered.forEach((x, i) => {
      const li = document.createElement('li');
      li.className = 'storefinder__item';
      li.dataset.index = String(i);

      const dist = x._dist != null ? `<span class="storefinder__item-dist">${x._dist.toFixed(1)} km</span>` : '';
      const addr = [x.street, [x.zip, x.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
      const tags = (x.products || [])
        .map((p) => `<span class="storefinder__tag">${esc(productLabels[p] || p)}</span>`)
        .join('');

      const badge = x.online ? ' <span class="storefinder__badge">🌐 Online-Händler</span>' : '';
      const meta = x.online && !addr
        ? (x.country ? `Versand nach ${esc(x.country)}` : 'Online-Versand')
        : `${esc(addr)}${x.type && !x.online ? ' · ' + esc(typeLabels[x.type] || x.type) : ''}`;

      li.innerHTML = `
        ${dist}
        <div class="storefinder__item-name">${esc(x.name)}${badge}</div>
        <div class="storefinder__item-meta">${meta}</div>
        ${x.phone ? `<div class="storefinder__item-meta"><a href="tel:${esc(x.phone)}">${esc(x.phone)}</a></div>` : ''}
        ${x.website ? `<div class="storefinder__item-meta"><a href="${esc(x.website)}" target="_blank" rel="noopener">Website</a></div>` : ''}
        ${tags ? `<div class="storefinder__item-tags">${tags}</div>` : ''}
        ${x.description ? `<details class="storefinder__desc">
          <summary>Weitere Informationen</summary>
          <div class="storefinder__desc-body">${x.description}</div>
        </details>` : ''}
      `;
      li.addEventListener('click', () => this.focusDealer(i));
      // Auf-/Zuklappen der Beschreibung darf nicht die Karte zentrieren.
      li.querySelector('.storefinder__desc')?.addEventListener('click', (e) => e.stopPropagation());
      list.appendChild(li);
    });

    this.renderMarkers();
    this.evaluateFallback();
  }

  /* ---------- Suche & Standort ---------- */

  async search() {
    const q = (this.refs.query?.value || '').trim();
    if (!q) return;
    this.setStatus('Suche …');
    try {
      const res = await fetch(`${this.apiBase}/geocode.php?q=${encodeURIComponent(q)}&region=de`);
      const data = await res.json();
      if (!data || !data.ok) { this.setStatus('Ort nicht gefunden.'); return; }
      this.origin = { lat: data.lat, lng: data.lng };
      this.setStatus('');
      this.afterOrigin(data.country);
    } catch (err) {
      this.setStatus('Suche fehlgeschlagen.');
    }
  }

  useGeolocation() {
    if (!navigator.geolocation) { this.setStatus('Standort nicht verfügbar.'); return; }
    this.setStatus('Standort wird ermittelt …');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        this.origin = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        this.setStatus('');
        this.afterOrigin(null); // Land unbekannt → Synergie nutzt DE-Default
      },
      () => this.setStatus('Standortfreigabe abgelehnt.'),
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }

  afterOrigin(country) {
    this.sortByDistance();
    this.render();
    if (this.map && this.origin) {
      this.map.setCenter(this.origin);
      this.map.setZoom(9);
    }
    this.showOnline(country);
  }

  /* ---------- Synergie: Online-Lieferdatum (§10/§11) ---------- */

  showOnline(country) {
    const region = regionForCountry(country) || 'DE';
    const result = calculateDelivery({
      days: this.daysByRegion[region],
      cutoffHour: this.cutoffHour,
      hideOverHours: this.hideOverHours,
      holidays: this.holidays,
      tz: this.tz,
    });
    const panel = this.refs.online;
    const dateEl = this.refs.onlineDate;
    if (!panel || !dateEl || !result) return;

    let text = `Zustellung voraussichtlich am ${result.deliveryDateFormatted}`;
    if (result.withinThreshold) text += ` (bei Bestellung innerhalb ${result.remainingFormatted})`;
    dateEl.innerHTML = `<strong>${esc(text)}.</strong>`;
  }

  /** Hebt die Online-Option hervor, wenn nichts in der Nähe ist. */
  evaluateFallback() {
    if (!this.origin) return;
    const nearest = this.filtered[0]?._dist;
    if (nearest == null || nearest > this.nearKm) {
      this.setStatus(`Kein Händler im Umkreis von ${this.nearKm} km – am schnellsten online.`);
      if (this.refs.online && !this.quietFallback) this.refs.online.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  /* ---------- Karte ---------- */

  async initMap() {
    try {
      await window.__sfMaps; // wird vom Maps-callback aufgelöst
    } catch (e) { return; }
    if (!window.google || !window.google.maps || !this.refs.map) return;

    // Karte erst bauen, wenn der Container sichtbar ist und echte Maße hat.
    // Sonst rechnet Google Maps für einen 0px-Viewport → grauer Kasten ohne
    // Kacheln (Section-Einblend-Animation, unter dem Fold, hinter Cookie-Banner).
    await this.whenMapHasSize();

    this.map = new google.maps.Map(this.refs.map, {
      center: this.center,
      zoom: this.zoom,
      mapTypeControl: false,
      streetViewControl: false,
    });
    this.info = new google.maps.InfoWindow();
    this.renderMarkers();

    // Der Container kann sich nachträglich noch ändern (Animation läuft aus,
    // Cookie-Banner verschwindet) → Karte neu vermessen und Ausschnitt halten.
    this.observeMapResize();
  }

  /** Löst auf, sobald der Karten-Container sichtbar ist und Breite/Höhe > 0 hat. */
  whenMapHasSize() {
    const el = this.refs.map;
    const hasSize = () => el.offsetWidth > 0 && el.offsetHeight > 0;
    if (hasSize()) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      let ro, io, timer;
      const finish = () => {
        if (done) return;
        done = true;
        ro?.disconnect();
        io?.disconnect();
        clearTimeout(timer);
        resolve();
      };
      ro = new ResizeObserver(() => { if (hasSize()) finish(); });
      ro.observe(el);
      io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting) && hasSize()) finish();
      });
      io.observe(el);
      // Sicherheitsnetz: nach 3 s trotzdem bauen (Karte holt sich per resize).
      timer = setTimeout(finish, 3000);
    });
  }

  /** Hält die Karte bei späteren Größenänderungen intakt. */
  observeMapResize() {
    const el = this.refs.map;
    let last = { w: el.offsetWidth, h: el.offsetHeight };
    this._mapRo = new ResizeObserver(() => {
      const w = el.offsetWidth;
      const h = el.offsetHeight;
      if (!this.map || !w || !h || (w === last.w && h === last.h)) return;
      last = { w, h };
      google.maps.event.trigger(this.map, 'resize');
      // Ausschnitt bewahren: nach Suche das Zentrum halten, sonst neu einpassen.
      if (this.origin) {
        this.map.setCenter(this.origin);
      } else if (this.markers.length) {
        this.renderMarkers(); // enthält fitBounds
      } else {
        this.map.setCenter(this.center);
      }
    });
    this._mapRo.observe(el);
  }

  disconnectedCallback() {
    this._mapRo?.disconnect();
  }

  renderMarkers() {
    if (!this.map || !window.google) return;
    this.markers.forEach((m) => m.setMap(null));
    this.markers = [];

    const bounds = new google.maps.LatLngBounds();
    this.filtered.forEach((x, i) => {
      if (x.lat == null || x.lng == null) return;
      const marker = new google.maps.Marker({ position: { lat: x.lat, lng: x.lng }, map: this.map, title: x.name });
      marker.addListener('click', () => this.openInfo(i, marker));
      this.markers.push(marker);
      bounds.extend(marker.getPosition());
    });
    if (this.origin) bounds.extend(this.origin);
    if (this.markers.length && !this.origin) this.map.fitBounds(bounds);
  }

  openInfo(i, marker) {
    const x = this.filtered[i];
    if (!x || !this.info) return;
    const addr = [x.street, [x.zip, x.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    this.info.setContent(`<strong>${esc(x.name)}</strong><br>${esc(addr)}`);
    this.info.open(this.map, marker);
  }

  focusDealer(i) {
    const x = this.filtered[i];
    if (!x) return;
    if (this.map && x.lat != null) {
      this.map.panTo({ lat: x.lat, lng: x.lng });
      this.map.setZoom(12);
      if (this.markers[i]) this.openInfo(i, this.markers[i]);
    }
  }
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
const toRad = (deg) => (deg * Math.PI) / 180;

/** Aktuelle Grobsaison: Winter = Nov–März, sonst Sommer. */
function currentSeason(date = new Date()) {
  const m = date.getMonth() + 1; // 1–12
  return (m >= 11 || m <= 3) ? 'winter' : 'sommer';
}

/**
 * Filter-Logik für die Verfügbarkeits-Auswahl.
 *   'now'         → ganzjährig + die in der aktuellen Saison geöffneten Händler
 *   'ganzjaehrig' → nur ganzjährig
 *   'winter'      → alles, was im Winter offen ist (ganzjährig + winter + weihnachten)
 */
function seasonMatches(filter, dealerSeason) {
  const s = dealerSeason || 'ganzjaehrig';
  if (filter === 'ganzjaehrig') return s === 'ganzjaehrig';
  if (filter === 'winter') return s === 'ganzjaehrig' || s === 'winter' || s === 'weihnachten';
  if (filter === 'now') {
    if (s === 'ganzjaehrig') return true;
    const m = new Date().getMonth() + 1;
    if (s === 'winter') return currentSeason() === 'winter';
    if (s === 'sommer') return currentSeason() === 'sommer';
    if (s === 'weihnachten') return m === 11 || m === 12;
    return true;
  }
  return true;
}

function esc(s) {
  return String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ));
}

if (!customElements.get('storefinder-app')) {
  customElements.define('storefinder-app', StorefinderApp);
}
